import React, { useState, useEffect } from "react";
import Layout from "./Layout";
import { getProgram } from "./Programs.slice";
import { useParams, Link } from "react-router-dom";
import Panel from "../../components/Panel";
import { formatUTCDate } from '../../util/string'
import DisplayField from '../../components/Form/DisplayField'
import CaptureVNEMSubmission from './CaptureVNEMSubmission'
import { IntervalDataReadinessChecklistItem, ReadinessChecklist } from '../../apis/types'
import ReadinessCheckList from './components/ReadinessChecklist'
import { getIntervalDataReadinessCheckListItemApi, getReadinessCheckListApi, updateProgramApi } from '../../apis/apis'
import { useAppDispatch, useAppSelector } from '../../shared/redux/hooks'
import { setNotesViewContext } from '../notes/notesViewContext'
import InlineEditInput from '../../components/Form/InlineEditInput'
import * as Yup from 'yup'
import Badge from '../../components/Badge'
import ControlledToggle from '../../components/Form/ControlledToggle'

const Program: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [readinessChecklist, setReadinessChecklist] = useState<ReadinessChecklist | null>(null)
  const [intervalDataReadinessChecklist, setIntervalDataReadinessChecklist] = useState<IntervalDataReadinessChecklistItem | null>(null)
  const [showCaptureVNEMSubmission, setShowCaptureVNEMSubmission] = useState(false)
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)

  const { programId } = useParams()
  const { program } = useAppSelector((state) => state.programs)

  const hasModifyPermission = permissions?.includes('program:manager:modify') ?? false

  const salesforceProgramLink: string | undefined =
    program?.salesforceId != null ? `https://kingenergy.lightning.force.com/lightning/r/Program__c/${program?.salesforceId}/view` : undefined

  const handleCaptureVNEMSubmission = (): void => {
    setShowCaptureVNEMSubmission(true)
    handleRefreshReadinessChecklist()
  }

  const handleRefreshReadinessChecklist = async (): Promise<void> => {
    void await getReadinessCheckListApi(programId as string).then((response) => {
      setReadinessChecklist(response as ReadinessChecklist)
    })
  }

  const handleRefreshIntervalDataReadinessChecklist = async (): Promise<void> => {
    if (programId == null) return
    const response = await getIntervalDataReadinessCheckListItemApi(programId)
    setIntervalDataReadinessChecklist(response ?? null)
  }

  const handleUpdateField = async (field: string, value: string | boolean | null): Promise<void> => {
    if (programId == null) return
    let updateData: any
    if (field === 'salesforceId') {
      updateData = { [field]: value ?? undefined }
    } else if (field === 'billingOn') {
      updateData = { [field]: value }
    } else {
      updateData = { [field]: value }
    }
    await updateProgramApi(programId, updateData)
    void dispatch(getProgram(programId))
  }

  useEffect(() => {
    void dispatch(getProgram(programId as string)).then(() => {
      setIsLoading(false)
    })
  }, [dispatch, programId])

  useEffect(() => {
    if (!programId) {
      setNotesViewContext(null)
      return
    }
    if (!program || program.id !== programId) {
      setNotesViewContext(null)
      return
    }
    setNotesViewContext({
      sourceEntity: `program|${programId}`,
      label: program.name,
    })
    return () => setNotesViewContext(null)
  }, [programId, program?.id, program?.name])

  // Every time an action is performed that will change the state of the readiness checklist (i.e. VNEM submission is captured), refresh the readiness checklist
  // (the checklist is refreshed differently for portal linking actions, see ReadinessChecklist.tsx)
  useEffect(() => {
    setReadinessChecklist(null)
    setIntervalDataReadinessChecklist(null)
    if (showCaptureVNEMSubmission) return
    setReadinessChecklist(null)
    setIntervalDataReadinessChecklist(null)
    void handleRefreshReadinessChecklist()
    void handleRefreshIntervalDataReadinessChecklist()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptureVNEMSubmission, programId])

  const renderDateField = (label: string, field: string, value: string | null | undefined) => {
    if (!hasModifyPermission) {
      return <DisplayField label={label}>{formatUTCDate(value as string)}</DisplayField>
    }
    return (
      <InlineEditInput
        name={field}
        label={label}
        initialValue={formatUTCDate(value as string)}
        handleSubmit={async (value) => await handleUpdateField(field, value)}
        validationSchema={Yup.string().matches(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{2}$/, 'Must be a valid date in MM/DD/YY format')}
      />
    )
  }

  const renderSalesforceIdField = () => {
    if (!hasModifyPermission) {
      return (
        <DisplayField label="Salesforce Program">
          {salesforceProgramLink != null ? (
            <div className="w-full">
              <a
                href={salesforceProgramLink}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 visited:text-purple-600 dark:visited:text-purple-400 underline hover:underline"
                title={program?.salesforceId}
              >
                {program?.salesforceName || program?.salesforceId}
              </a>
            </div>
          ) : (
            'Not set'
          )}
        </DisplayField>
      )
    }
    return (
      <InlineEditInput
        name="salesforceId"
        label="Salesforce Program"
        initialValue={program?.salesforceId ?? ''}
        displayValue={program?.salesforceName ?? undefined}
        handleSubmit={async (value) => await handleUpdateField('salesforceId', value)}
        validationSchema={Yup.string().matches(/^(?:[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})$/, 'Must be 15 or 18 characters')}
        linkTemplate="https://kingenergy.lightning.force.com/lightning/r/Program__c/@@value@@/view"
      />
    )
  }

  return (
    <>
      <Layout title={program?.name ?? 'Program'}>
        {isLoading && <div>Loading...</div>}
        {!isLoading && (
          <div>
            <Panel header="Program Summary" className="mb-5">
              <div className="grid grid-cols-4 gap-4 m-2">
                <DisplayField label="Name">{program?.name}</DisplayField>
                <DisplayField label="Utility">{program?.utility}</DisplayField>
                {renderDateField('Start Date', 'startDate', program?.startDate)}
                {program?.endDate != null && renderDateField('End Date', 'endDate', program?.endDate)}
                {renderDateField('PTO Date', 'ptoDate', program?.ptoDate)}
                {renderDateField('VNEM Transition Start Date', 'billingOnDate', program?.billingOnDate)}
                {hasModifyPermission ? (
                  <div className="flex flex-col flex-start">
                    <div className="font-medium color-base text-slate-700 dark:text-slate-400 mb-1">VNEM Billing Active</div>
                    <ControlledToggle
                      checked={program?.billingOn ?? false}
                      onChange={(isChecked) => handleUpdateField('billingOn', isChecked)}
                    />
                  </div>
                ) : (
                  <DisplayField label="VNEM Billing Active">
                    <div className="w-[100%] flex justify-start">
                      <Badge status={program?.billingOn ? 'YES' : 'NO'} className="w-[30%] justify-center" />
                    </div>
                  </DisplayField>
                )}
                <div className="col-span-2">{renderSalesforceIdField()}</div>
              </div>
            </Panel>

            <div className="mb-5">
              <Link
                to={`/program/programs/${programId}/reconciliation`}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-50 dark:bg-indigo-400/10 px-4 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-400 ring-1 ring-inset ring-indigo-700/10 dark:ring-indigo-400/20 hover:bg-indigo-100 dark:hover:bg-indigo-400/20 transition-colors"
              >
                View Reconciliation Report
              </Link>
            </div>

            {showCaptureVNEMSubmission && programId ? (
              <CaptureVNEMSubmission
                programId={programId}
                onCaptureVNEMSubmission={() => setShowCaptureVNEMSubmission(false)}
                setReadinessCheckList={setReadinessChecklist}
                onFailureToFetchVNEMSubmission={() => setShowCaptureVNEMSubmission(false)}
              />
            ) : (
              readinessChecklist == null ? (<div>Loading readiness checklist...</div>) : (
                <ReadinessCheckList
                  program={program}
                  readinessChecklist={{
                    ...readinessChecklist,
                    intervalDataStatus: intervalDataReadinessChecklist ?? undefined,
                  }}
                  handleCaptureVNEMSubmission={handleCaptureVNEMSubmission}
                  handleRefreshReadinessChecklist={handleRefreshReadinessChecklist}
                  handleRefreshIntervalDataReadinessChecklist={handleRefreshIntervalDataReadinessChecklist}
                />
              )
            )}
          </div>
        )}
      </Layout>
    </>
  )
}

export default Program;
